import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../components/01_config/api";

// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

export const fetchVocabWords = createAsyncThunk(
    "vocab/fetchVocabWords",
    async ({ lessonId }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;
        const role = state.auth?.user?.role;
        const selectedStudentId = state.teacher?.selectedStudentId;
        const lessonQuery = lessonId === "all" ? "" : `?lessonId=${lessonId}`;

        if (!token) {
            try {
                const res = await fetch(`${API_URL}/public/words${lessonQuery}`);
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    return thunkAPI.rejectWithValue(body.error || "Failed to load words");
                }

                const normalized = (Array.isArray(body) ? body : []).map((w) => ({
                    id: w.id,
                    lessonId: w.lessonId,
                    word: w.word,
                    word_context: w.wordContext,
                    translat: w.translat,
                    translat_context: w.translatContext,
                    dictionary: w.dictionary,
                    correctStudent: false,
                    correctTutor: false,
                    input: "",
                    display_translat: "hidden",
                }));

                return { lessonId, words: normalized };
            } catch (err) {
                return thunkAPI.rejectWithValue(err.message || "Network error");
            }
        }

        const isTeacherMode = role === "TEACHER";

        if (isTeacherMode && !selectedStudentId) {
            return thunkAPI.rejectWithValue("No student selected");
        }

        const url = isTeacherMode
            ? `${API_URL}/teacher/students/${selectedStudentId}/words${lessonQuery}`
            : `${API_URL}/me/words${lessonQuery}`;

        try {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const body = await res.json().catch(() => ({}));

            if (!res.ok) {
                return thunkAPI.rejectWithValue(body.error || "Failed to load words");
            }

            // Student returns array; teacher returns { student, words }
            const words = Array.isArray(body) ? body : body.words || [];

            // Map backend fields to what Frame currently expects:
            // correctStudent / correctTutor flags + keep word/dictionary fields.

            const normalized = words.map((w) => ({
                id: w.id,
                lessonId: w.lessonId,
                word: w.word,
                word_context: w.wordContext,
                translat: w.translat,
                translat_context: w.translatContext,
                dictionary: w.dictionary,
                correctStudent: !!w.isStudentDone,
                correctTutor: !!w.isTutorDone,
                input: "",
                display_translat: "hidden",
            }));

            let filtered = normalized;

            // ✅ STUDENT: keep only words from unlocked lessons
            if (role === "STUDENT") {
                const unlockedLessonIds = (state.studentLessons?.lessons || [])
                    .filter((l) => l.isUnlocked)
                    .map((l) => l.id);

                filtered = normalized.filter((w) =>
                    unlockedLessonIds.includes(w.lessonId)
                );
            }

            return { lessonId, words: filtered };

        } catch (err) {
            return thunkAPI.rejectWithValue(err.message || "Network error");
        }
    }
);

//-------------------

export const toggleStudentWord = createAsyncThunk(
    "vocab/toggleStudentWord",
    async ({ wordId, nextValue }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) return thunkAPI.rejectWithValue("Not authenticated");

        const res = await fetch(`${API_URL}/me/words/${wordId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ isStudentDone: nextValue }),
        });

        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
            return thunkAPI.rejectWithValue(body.error || "Failed to update word");
        }

        return { wordId, nextValue };
    }
);



export const toggleTutorWord = createAsyncThunk(
    "vocab/toggleTutorWord",
    async ({ studentId, wordId, nextValue }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) return thunkAPI.rejectWithValue("Not authenticated");

        const res = await fetch(
            `${API_URL}/teacher/students/${studentId}/words/${wordId}`,
            {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ isTutorDone: nextValue }),
            }
        );

        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
            return thunkAPI.rejectWithValue(body.error || "Failed to update word");
        }

        return { wordId, nextValue };
    }
);

//---------------------

const vocabSlice = createSlice({
    name: "vocab",
    initialState: {
        lessonId: 1,
        words: [],
        loading: false,
        error: null,
    },
    reducers: {
        setWordInput(state, action) {
            const { wordId, text } = action.payload;
            const w = state.words.find((x) => x.id === wordId);
            if (w) w.input = text;
        },
        toggleWordReveal(state, action) {
            const wordId = action.payload;
            const w = state.words.find((x) => x.id === wordId);
            if (w) w.display_translat = w.display_translat === "hidden" ? "visible" : "hidden";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVocabWords.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVocabWords.fulfilled, (state, action) => {
                state.loading = false;

                const allWords = action.payload.words;
                const role = state.auth?.user?.role;

                if (role === "STUDENT") {
                    const unlockedLessonIds = (state.studentLessons?.lessons || [])
                        .filter((l) => l.isUnlocked)
                        .map((l) => l.id);

                    state.words = allWords.filter((w) =>
                        // unlockedLessonIds.includes(1)
                        unlockedLessonIds.includes(w.lessonId)
                    );
                    console.log("vocab words after filter:", state.words.length)
                } else {
                    // TEACHER / ADMIN
                    state.words = allWords;
                }
            })
            .addCase(fetchVocabWords.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to load vocab";
            })
            .addCase(toggleStudentWord.fulfilled, (state, action) => {
                const { wordId, nextValue } = action.payload;
                const w = state.words.find((x) => x.id === wordId);
                if (w) w.correctStudent = nextValue;
            })
            .addCase(toggleTutorWord.fulfilled, (state, action) => {
                const { wordId, nextValue } = action.payload;
                const w = state.words.find((x) => x.id === wordId);
                if (w) w.correctTutor = nextValue;
            })
    },
});

export const { setWordInput, toggleWordReveal } = vocabSlice.actions;
export default vocabSlice.reducer;
